import TextField from '@mui/material/TextField';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import { UPDATE_CATEGORY_RESET } from '../../constants/categoryConstants';
import { updateCategory, clearErrors, getCategories, getCategoryDetails } from '../../actions/categoryAction';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';
import { useTheme, styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { Button, Box, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const UpdateCategory = () => {

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const theme = useTheme();
    const params = useParams();

    const { loading, isUpdated, error } = useSelector((state) => state.updateCategory);
    const { category } = useSelector((state) => state.categoryDetails);
    const { categories } = useSelector((state) => state.categories);

    const [name, setName] = useState("");
    const [parent, setParent] = useState("");
    const [logo, setLogo] = useState("");
    const [oldLogo, setOldLogo] = useState("");
    const [logoPreview, setLogoPreview] = useState("");

    const handleLogoChange = (e) => {
        const reader = new FileReader();

        reader.onload = () => {
            if (reader.readyState === 2) {
                setLogoPreview(reader.result);
                setLogo(reader.result);
            }
        };

        reader.readAsDataURL(e.target.files[0]);
    }

    const updateCategorySubmitHandler = (e) => {
        e.preventDefault();

        if (!name.trim()) {
            enqueueSnackbar("Category Name is required", { variant: "warning" });
            return;
        }

        const categoryData = {
            name,
            parent,
        };

        if (logo) {
            categoryData.logo = logo;
        }

        dispatch(updateCategory(params.id, categoryData));
    }

    const categoryId = params.id;

    useEffect(() => {
        if (category && category._id !== categoryId) {
            dispatch(getCategoryDetails(categoryId));
        } else if (category) {
            setName(category.name);
            setParent(category.parent?._id || "");
            setOldLogo(category.logo?.url);
        }
        dispatch(getCategories());
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        if (isUpdated) {
            enqueueSnackbar("Category Updated", { variant: "success" });
            dispatch({ type: UPDATE_CATEGORY_RESET });
            navigate("/admin/categories");
        }
    }, [dispatch, error, isUpdated, navigate, enqueueSnackbar, category, categoryId]);

    return (
        <>
            <MetaData title="Admin: Update Category | Flipkart" />

            {loading && <BackdropLoader />}
            <motion.form
                initial={{ opacity: 0, x: -100 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                onSubmit={updateCategorySubmitHandler}
                encType="multipart/form-data"
                className="flex flex-col sm:flex-row rounded-lg shadow p-4"
                id="mainform"
                style={{ backgroundColor: theme.palette.background.paper }}
            >

                <div className="flex flex-col gap-3 m-2 sm:w-1/2">
                    <TextField
                        label="Name"
                        variant="outlined"
                        size="small"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <TextField
                        label="Parent Category"
                        select
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={parent}
                        onChange={(e) => setParent(e.target.value)}
                    >
                        <MenuItem value="">None</MenuItem>
                        {categories && categories.map((el, i) => (
                            <MenuItem value={el._id} key={i}>
                                {el.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    <div className="flex flex-col gap-2">
                        <h2 className="font-medium">Category Logo</h2>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 100, height: 100, border: '2px dashed grey', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {logoPreview ? 
                                <img draggable="false" src={logoPreview} alt="Category Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : 
                                (oldLogo ? <img draggable="false" src={oldLogo} alt="Category Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <ImageIcon sx={{ fontSize: 40, color: 'grey.500' }} />)
                            }
                        </Box>
                        <Button
                            component="label"
                            variant="contained"
                            startIcon={<ImageIcon />}
                        >
                            Upload Logo
                            <VisuallyHiddenInput type="file" accept="image/*" onChange={handleLogoChange} />
                        </Button>
                    </Box>
                    </div>

                    <div className="flex justify-end">
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                type="submit"
                                form="mainform"
                                size="large"
                                sx={{ bgcolor: theme.palette.primary.main, width: '10rem' }}
                            >
                                Update
                            </Button>
                        </motion.div>
                    </div>

                </div>

            </motion.form>
        </>
    );
};

export default UpdateCategory;
